import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft, Crown, Shield, Users as UsersIcon, BarChart3,
  Search, Loader2, Trash2, Plus, ShieldCheck, ShieldOff, MapPin, TrendingUp,
  Ban, UserX, Utensils, BedDouble, Landmark, FileDown, FileText, ExternalLink,
  Sparkles, ClipboardList, Headphones,
} from "lucide-react";

import { useAuth } from "@/hooks/use-auth";
import { useAdmin } from "@/hooks/use-admin";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import {
  listUsers, grantPremium, revokePremium, grantCredits,
  grantPro, revokePro,
  getUserEntitlementDiagnostics,
  setFounderPreviewTier,
  listAdmins, addAdmin, removeAdmin, getStats,
  deleteUser, setUserBan, getTopPlaces, getPlaceDetail,
  getMealDefaults, setMealDefaults,
} from "@/lib/admin.functions";
import { grantConciergeAgent, revokeConciergeAgent } from "@/lib/concierge.functions";
import { clearPremiumCache } from "@/lib/premium";
import { getStripeEnvironment } from "@/lib/stripe";


import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { jsPDF } from "jspdf";

export const Route = createFileRoute("/admin")({ component: AdminPage });

type Tab = "users" | "stats" | "team";

function AdminPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isAdmin, isOwner, loading: roleLoading } = useAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("users");

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      storePostLoginRedirect();
      navigate({ to: "/login" });
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading || roleLoading) {
    return (
      <main className="mx-auto flex min-h-[70vh] w-full max-w-5xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto min-h-[70vh] w-full max-w-2xl px-4 py-16 text-center">
        <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 text-2xl font-bold">Acesso restrito</h1>
        <p className="mt-2 text-sm text-muted-foreground">Esta área é só para administradores.</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-1 text-sm text-brand hover:underline">
          <ArrowLeft className="h-4 w-4" /> Voltar ao início
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-[70vh] w-full max-w-5xl overflow-x-hidden px-4 py-6">
      <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-brand" />
        <h1 className="text-2xl font-bold tracking-tight">Painel de administração</h1>
        {isOwner && (
          <span className="ml-2 rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">
            Owner
          </span>
        )}
        <Link
          to="/concierge/admin"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-input px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <ClipboardList className="h-3.5 w-3.5" /> Pedidos concierge
        </Link>
      </div>

      <nav className="mt-6 flex gap-1 overflow-x-auto border-b border-border">
        <TabBtn active={tab === "users"} onClick={() => setTab("users")} icon={<UsersIcon className="h-4 w-4" />}>Utilizadores</TabBtn>
        <TabBtn active={tab === "stats"} onClick={() => setTab("stats")} icon={<BarChart3 className="h-4 w-4" />}>Estatísticas</TabBtn>
        <TabBtn active={tab === "team"} onClick={() => setTab("team")} icon={<Shield className="h-4 w-4" />}>Equipa</TabBtn>
      </nav>

      <div className="mt-6">
        {tab === "users" && <UsersTab />}
        {tab === "stats" && <StatsTab />}
        {tab === "team" && <TeamTab />}
      </div>
    </main>
  );
}

function TabBtn({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition ${
        active ? "border-brand text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon} {children}
    </button>
  );
}

// ============================================================
// USERS TAB
// ============================================================
function UsersTab() {
  const listFn = useServerFn(listUsers);
  const grantFn = useServerFn(grantPremium);
  const revokeFn = useServerFn(revokePremium);
  const grantProFn = useServerFn(grantPro);
  const revokeProFn = useServerFn(revokePro);
  const diagnosticsFn = useServerFn(getUserEntitlementDiagnostics);
  const previewTierFn = useServerFn(setFounderPreviewTier);
  const { user: currentUser } = useAuth();
  const grantAgentFn = useServerFn(grantConciergeAgent);
  const revokeAgentFn = useServerFn(revokeConciergeAgent);
  const grantCreditsFn = useServerFn(grantCredits);
  const deleteFn = useServerFn(deleteUser);
  const banFn = useServerFn(setUserBan);
  const [users, setUsers] = useState<Awaited<ReturnType<typeof listUsers>>["users"]>([]);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [inactiveDays, setInactiveDays] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [grantUserId, setGrantUserId] = useState<string | null>(null);
  const [creditsUserId, setCreditsUserId] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<Awaited<ReturnType<typeof getUserEntitlementDiagnostics>> | null>(null);
  const [diagnosticsLoadingId, setDiagnosticsLoadingId] = useState<string | null>(null);
  const [diagnosticsUserId, setDiagnosticsUserId] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  /** Uma revogação nunca pode ficar presa em cache local do próprio Fundador. */
  const dropLocalPlanCache = useCallback((userId: string) => {
    if (currentUser?.id === userId) clearPremiumCache(userId, getStripeEnvironment());
  }, [currentUser]);

  const load = useCallback(async (q?: string, inactive?: number | null) => {
    setLoading(true);
    try {
      const res = await listFn({ data: { search: q, limit: 200, inactiveDays: inactive ?? null } });
      setUsers(res.users);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [listFn]);

  useEffect(() => { load(search, inactiveDays); }, [load, inactiveDays]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRevoke(userId: string) {
    setBusyId(userId);
    try {
      await revokeFn({ data: { userId } });
      dropLocalPlanCache(userId);
      toast.success("Premium manual revogado");
      await load(search, inactiveDays);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  async function handleGrant(userId: string, days: number | null, reason: string) {
    setBusyId(userId);
    try {
      await grantFn({ data: { userId, days, reason: reason || undefined } });
      toast.success("Premium concedido");
      setGrantUserId(null);
      await load(search, inactiveDays);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  async function handleTogglePro(userId: string, isPro: boolean) {
    setBusyId(userId);
    try {
      if (isPro) {
        await revokeProFn({ data: { userId } });
        dropLocalPlanCache(userId);
        toast.success("Pro removido");
      } else {
        await grantProFn({ data: { userId } });
        dropLocalPlanCache(userId);
        toast.success("Pro concedido");
      }
      await load(search, inactiveDays);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  async function handleToggleAgent(userId: string, isAgent: boolean) {
    setBusyId(userId);
    try {
      if (isAgent) {
        if (!confirm("Remover este colaborador de Roteiros Personalizados? Os pedidos atribuídos ficarão sem responsável.")) {
          setBusyId(null);
          return;
        }
        await revokeAgentFn({ data: { userId } });
        toast.success("Colaborador removido");
      } else {
        await grantAgentFn({ data: { userId } });
        toast.success("Colaborador de concierge adicionado");
      }
      await load(search, inactiveDays);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  async function handleDiagnostics(userId: string) {
    setDiagnosticsLoadingId(userId);
    setDiagnosticsUserId(userId);
    try {
      setDiagnostics(await diagnosticsFn({ data: { userId } }));
    } catch (e) {
      toast.error((e as Error).message || "Não foi possível obter o diagnóstico");
    } finally {
      setDiagnosticsLoadingId(null);
    }
  }



  async function handleGrantCredits(userId: string, amount: number, reason: string) {
    setBusyId(userId);
    try {
      await grantCreditsFn({ data: { userId, amount, reason: reason || undefined } });
      toast.success(`${amount} rota${amount === 1 ? "" : "s"} adicionada${amount === 1 ? "" : "s"}`);
      setCreditsUserId(null);
      await load(search, inactiveDays);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  async function handleDelete(userId: string, email: string) {
    if (!confirm(`Apagar permanentemente a conta de ${email || userId}?\n\nIsto remove o utilizador, as rotas guardadas e os créditos. Não pode ser revertido.`)) return;
    setBusyId(userId);
    try {
      await deleteFn({ data: { userId } });
      toast.success("Conta apagada");
      await load(search, inactiveDays);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  async function handleBanToggle(userId: string, banned: boolean) {
    setBusyId(userId);
    try {
      await banFn({ data: { userId, banned } });
      toast.success(banned ? "Conta suspensa" : "Suspensão removida");
      await load(search, inactiveDays);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  return (
    <div>
      <form
        onSubmit={(e) => { e.preventDefault(); load(search, inactiveDays); }}
        className="mb-3 flex items-center gap-2"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Procurar por email..."
            className="w-full rounded-full border border-input bg-background py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>
        <button type="submit" className="rounded-full bg-brand px-4 py-2 text-sm font-medium text-brand-foreground hover:opacity-90">
          Procurar
        </button>
      </form>

      <div className="mb-4 flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">Inativos há:</span>
        {[
          { label: "Todos", v: null },
          { label: "30 dias", v: 30 },
          { label: "60 dias", v: 60 },
          { label: "90 dias", v: 90 },
          { label: "180 dias", v: 180 },
        ].map((opt) => (
          <button
            key={String(opt.v)}
            type="button"
            onClick={() => setInactiveDays(opt.v)}
            className={`rounded-full border px-2.5 py-1 ${
              inactiveDays === opt.v ? "border-brand bg-brand/10 text-brand" : "border-input text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : users.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Nenhum utilizador encontrado.</p>
      ) : (
        <div className="space-y-2">
          {users.map((u) => {
            const isBanned = !!u.bannedUntil && new Date(u.bannedUntil) > new Date();
            return (
            <div key={u.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium">{u.email || "(sem email)"}</span>
                    {u.isOwner && <Badge tone="brand">Owner</Badge>}
                    {!u.isOwner && u.isAdmin && <Badge tone="brand">Admin</Badge>}
                    {u.isPremium && !u.isAdmin && <Badge tone="gold">Premium</Badge>}
                    {u.isPro && <Badge tone="purple">Pro</Badge>}
                    {u.manualGrant && <Badge tone="purple">Concessão manual</Badge>}
                    {u.isConciergeAgent && <Badge tone="brand">Concierge</Badge>}
                    {isBanned && <Badge tone="red">Suspenso</Badge>}

                  </div>
                  {u.displayName && <p className="text-xs text-muted-foreground">{u.displayName}</p>}
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span>Registo: {new Date(u.createdAt).toLocaleDateString("pt-PT")}</span>
                    <span>
                      Último login: {u.lastSignInAt ? new Date(u.lastSignInAt).toLocaleDateString("pt-PT") : "nunca"}
                    </span>
                    <span>Guardadas: {u.routesCount}</span>
                    <span>Geradas IA: {u.generatedCount ?? 0}</span>

                    {u.subscription && (
                      <span>
                        Sub: {u.subscription.status} ({u.subscription.environment})
                        {u.subscription.currentPeriodEnd && ` até ${new Date(u.subscription.currentPeriodEnd).toLocaleDateString("pt-PT")}`}
                      </span>
                    )}
                    {u.manualGrant?.expiresAt && (
                      <span>Manual até {new Date(u.manualGrant.expiresAt).toLocaleDateString("pt-PT")}</span>
                    )}
                    {u.manualGrant && !u.manualGrant.expiresAt && <span>Manual vitalício</span>}
                  </div>
                </div>
                <div className="flex w-full flex-wrap justify-start gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
                  {u.manualGrant ? (
                    <button
                      type="button"
                      onClick={() => handleRevoke(u.id)}
                      disabled={busyId === u.id}
                      className="rounded-full border border-input px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
                    >
                      {busyId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Revogar premium"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setGrantUserId(u.id)}
                      disabled={busyId === u.id || u.isAdmin}
                      title={u.isAdmin ? "Admins já têm premium" : ""}
                      className="rounded-full bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
                    >
                      <Crown className="mr-1 inline h-3 w-3" /> Dar premium
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleTogglePro(u.id, u.isPro)}
                    disabled={busyId === u.id}
                    title={u.isPro ? "Remover acesso Pro" : "Dar acesso Routify Pro"}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                      u.isPro
                        ? "border border-input hover:bg-accent"
                        : "bg-purple-600 text-white hover:bg-purple-700"
                    }`}
                  >
                    <Sparkles className="h-3 w-3" /> {u.isPro ? "Remover Pro" : "Dar Pro"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDiagnostics(u.id)}
                    disabled={diagnosticsLoadingId === u.id}
                    className="inline-flex items-center gap-1 rounded-full border border-input px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
                    title="Ver a decisão efetiva de plano"
                  >
                    {diagnosticsLoadingId === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                    Diagnóstico
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleAgent(u.id, u.isConciergeAgent)}
                    disabled={busyId === u.id}
                    title={u.isConciergeAgent ? "Remover como colaborador" : "Tornar colaborador de Roteiros Personalizados"}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                      u.isConciergeAgent
                        ? "border border-input hover:bg-accent"
                        : "bg-teal-600 text-white hover:bg-teal-700"
                    }`}
                  >
                    <Headphones className="h-3 w-3" /> {u.isConciergeAgent ? "Remover Concierge" : "Dar Concierge"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setCreditsUserId(u.id)}
                    disabled={busyId === u.id}
                    title="Adicionar rotas (créditos) — para parcerias"
                    className="inline-flex items-center gap-1 rounded-full border border-input px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
                  >
                    <Plus className="h-3 w-3" /> Dar rotas
                  </button>
                  {!u.isOwner && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleBanToggle(u.id, !isBanned)}
                        disabled={busyId === u.id}
                        title={isBanned ? "Reativar conta" : "Suspender (bloquear login)"}
                        className="inline-flex items-center gap-1 rounded-full border border-input px-3 py-1.5 text-xs hover:bg-accent disabled:opacity-50"
                      >
                        <Ban className="h-3 w-3" /> {isBanned ? "Reativar" : "Suspender"}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(u.id, u.email)}
                        disabled={busyId === u.id}
                        title="Apagar conta (permanente)"
                        className="inline-flex items-center gap-1 rounded-full border border-input px-3 py-1.5 text-xs hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"
                      >
                        <UserX className="h-3 w-3" /> Apagar
                      </button>
                    </>
                  )}
                </div>
              </div>

              {grantUserId === u.id && (
                <GrantForm
                  onCancel={() => setGrantUserId(null)}
                  onSubmit={(days, reason) => handleGrant(u.id, days, reason)}
                  busy={busyId === u.id}
                />
              )}
              {creditsUserId === u.id && (
                <CreditsForm
                  onCancel={() => setCreditsUserId(null)}
                  onSubmit={(amount, reason) => handleGrantCredits(u.id, amount, reason)}
                  busy={busyId === u.id}
                />
              )}
            </div>
            );
          })}
        </div>
      )}
      <Dialog open={diagnostics !== null} onOpenChange={(open) => !open && setDiagnostics(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Diagnóstico de acesso</DialogTitle>
          </DialogHeader>
          {diagnostics ? (
            <div className="space-y-4 text-sm">
              <div className="grid gap-2 sm:grid-cols-3">
                <DiagnosticValue label="Plano efetivo" value={diagnostics.plan.toUpperCase()} />
                <DiagnosticValue label="Origem" value={diagnostics.entitlement.source} />
                <DiagnosticValue label="Válido até" value={diagnostics.entitlement.validUntil ? new Date(diagnostics.entitlement.validUntil).toLocaleString("pt-PT") : "Sem expiração"} />
              </div>
              <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                Decisão: Stripe {diagnostics.entitlement.stripeTier}, concessões {diagnostics.entitlement.grantTier}. Os papéis Owner/Admin dão apenas permissões administrativas: sem subscrição nem concessão, o plano comercial efetivo é Free.
              </p>
              {diagnosticsUserId && diagnosticsUserId === currentUser?.id && (diagnostics.roles as Array<{ role: string }>).some((r) => r.role === "owner" || r.role === "admin") ? (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
                  <p className="text-xs font-semibold">Modo de simulação (apenas esta conta)</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Simula Free, Premium ou Pro na tua própria conta e neste ambiente. Não altera subscrições reais,
                    nunca afeta clientes nem outros administradores, e “Desligar” devolve o plano comercial verdadeiro.
                  </p>

                  <div className="mt-2 flex flex-wrap gap-2">
                    {(["free", "premium", "pro"] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        disabled={previewBusy}
                        onClick={async () => {
                          setPreviewBusy(true);
                          try {
                            await previewTierFn({ data: { userId: diagnosticsUserId, tier: t } });
                            dropLocalPlanCache(diagnosticsUserId);
                            setDiagnostics(await diagnosticsFn({ data: { userId: diagnosticsUserId } }));
                            toast.success(`Modo de simulação: ${t.toUpperCase()}`);
                          } catch (e) { toast.error((e as Error).message); }
                          finally { setPreviewBusy(false); }
                        }}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                          diagnostics.entitlement.source === "founder_preview" && diagnostics.plan === t
                            ? "border-brand bg-brand/10 text-brand"
                            : "border-input hover:bg-accent"
                        }`}
                      >
                        {t.toUpperCase()}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={previewBusy}
                      onClick={async () => {
                        setPreviewBusy(true);
                        try {
                          await previewTierFn({ data: { userId: diagnosticsUserId, tier: null } });
                          dropLocalPlanCache(diagnosticsUserId);
                          setDiagnostics(await diagnosticsFn({ data: { userId: diagnosticsUserId } }));
                          toast.success("Simulação desligada — plano comercial real reposto");
                        } catch (e) { toast.error((e as Error).message); }
                        finally { setPreviewBusy(false); }
                      }}
                      className="rounded-full border border-input px-3 py-1 text-xs font-semibold hover:bg-accent"
                    >
                      Desligar
                    </button>
                  </div>
                </div>
              ) : null}
              <DiagnosticList label="Papéis" rows={diagnostics.roles} empty="Sem papéis" />
              <DiagnosticList label="Subscrições" rows={diagnostics.subscriptions} empty="Sem subscrições" />
              <DiagnosticList label="Concessões manuais" rows={diagnostics.grants} empty="Sem concessões" />
              {diagnostics.errors.length > 0 ? <p className="text-xs text-destructive">Erros de leitura: {diagnostics.errors.join("; ")}</p> : null}
              <p className="text-[11px] text-muted-foreground">Verificado em {new Date(diagnostics.checkedAt).toLocaleString("pt-PT")}</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DiagnosticValue({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg border border-border p-3"><p className="text-[11px] text-muted-foreground">{label}</p><p className="font-semibold">{value}</p></div>;
}

function DiagnosticList({ label, rows, empty }: { label: string; rows: unknown[]; empty: string }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold">{label}</p>
      <pre className="max-h-40 overflow-auto rounded-lg border border-border bg-muted/40 p-3 text-[11px]">{rows.length ? JSON.stringify(rows, null, 2) : empty}</pre>
    </div>
  );
}

function GrantForm({ onCancel, onSubmit, busy }: { onCancel: () => void; onSubmit: (days: number | null, reason: string) => void; busy: boolean }) {
  const [days, setDays] = useState<string>("30");
  const [reason, setReason] = useState("");
  return (
    <div className="mt-3 space-y-2 rounded-lg bg-muted/40 p-3">
      <div className="flex flex-wrap gap-2">
        {[
          { label: "7 dias", v: "7" }, { label: "30 dias", v: "30" },
          { label: "1 ano", v: "365" }, { label: "Vitalício", v: "lifetime" },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setDays(opt.v)}
            className={`rounded-full border px-3 py-1 text-xs ${
              days === opt.v ? "border-brand bg-brand/10 text-brand" : "border-input"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Motivo (opcional)"
        maxLength={280}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
      />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-full border border-input px-3 py-1.5 text-xs">Cancelar</button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onSubmit(days === "lifetime" ? null : Number(days), reason)}
          className="rounded-full bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Conceder"}
        </button>
      </div>
    </div>
  );
}

function Badge({ tone, children }: { tone: "brand" | "gold" | "purple" | "red"; children: React.ReactNode }) {
  const cls =
    tone === "brand" ? "bg-brand/15 text-brand"
    : tone === "gold" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
    : tone === "red" ? "bg-destructive/15 text-destructive"
    : "bg-purple-500/15 text-purple-700 dark:text-purple-400";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>{children}</span>;
}

// ============================================================
// STATS TAB
// ============================================================
function CreditsForm({ onCancel, onSubmit, busy }: { onCancel: () => void; onSubmit: (amount: number, reason: string) => void; busy: boolean }) {
  const [amount, setAmount] = useState<string>("5");
  const [reason, setReason] = useState("");
  return (
    <div className="mt-3 space-y-2 rounded-lg bg-muted/40 p-3">
      <div className="flex flex-wrap gap-2">
        {["1", "5", "10", "25", "50", "100"].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setAmount(v)}
            className={`rounded-full border px-3 py-1 text-xs ${amount === v ? "border-brand bg-brand/10 text-brand" : "border-input"}`}
          >
            +{v}
          </button>
        ))}
        <input
          type="number"
          min={1}
          max={10000}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-24 rounded-full border border-input bg-background px-3 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand"
          placeholder="Qtd"
        />
      </div>
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Motivo (ex.: parceria X)"
        maxLength={280}
        className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand"
      />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-full border border-input px-3 py-1 text-xs">Cancelar</button>
        <button
          type="button"
          disabled={busy || !Number.isFinite(Number(amount)) || Number(amount) < 1}
          onClick={() => onSubmit(Math.floor(Number(amount)), reason)}
          className="rounded-full bg-brand px-3 py-1 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Adicionar rotas"}
        </button>
      </div>
    </div>
  );
}

function StatsTab() {
  const statsFn = useServerFn(getStats);
  const [stats, setStats] = useState<Awaited<ReturnType<typeof getStats>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => statsFn().then(setStats).catch((e) => toast.error((e as Error).message)).finally(() => setLoading(false));
    load();
    // Auto-refresh live activity every 30s while the tab is open.
    const id = window.setInterval(load, 30_000);
    return () => window.clearInterval(id);
  }, [statsFn]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!stats) return null;

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
          </span>
          Atividade em direto
        </h2>
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Agora (5 min)" value={stats.activeNow} />
          <StatCard label="Hoje (24h)" value={stats.activeToday} />
          <StatCard label="Últimos 7 dias" value={stats.activeLast7Days} />
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Utilizadores" value={stats.totalUsers} icon={<UsersIcon className="h-4 w-4" />} />
        <StatCard label="Rotas guardadas" value={stats.totalRoutes} icon={<MapPin className="h-4 w-4" />} />
        <StatCard label="Premium ativos (live)" value={stats.activeLiveSubscriptions} icon={<Crown className="h-4 w-4" />} />
        <StatCard label="Receita mensal (€)" value={stats.monthlyRevenueEUR.toFixed(2)} icon={<TrendingUp className="h-4 w-4" />} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard label="Rotas (últimos 7 dias)" value={stats.routesLast7Days} />
        <StatCard label="Rotas (últimos 30 dias)" value={stats.routesLast30Days} />
      </div>

      <section className="rounded-xl border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold">Top 10 cidades</h2>
        {stats.topCities.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sem dados ainda.</p>
        ) : (
          <ul className="space-y-1.5">
            {stats.topCities.map((c, i) => (
              <li key={c.city} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-5 text-xs text-muted-foreground">#{i + 1}</span>
                  {c.city}
                </span>
                <span className="text-muted-foreground">{c.count}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <TopPlacesSection />

      <MealDefaultsCard />
    </div>
  );
}

function MealDefaultsCard() {
  const getFn = useServerFn(getMealDefaults);
  const saveFn = useServerFn(setMealDefaults);
  const [prefs, setPrefs] = useState<Awaited<ReturnType<typeof getMealDefaults>> | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getFn().then(setPrefs).catch(() => {}); }, [getFn]);

  if (!prefs) {
    return (
      <section className="rounded-xl border border-border bg-card p-4">
        <h2 className="mb-2 inline-flex items-center gap-2 text-sm font-semibold"><Utensils className="h-4 w-4" /> Preferências de refeição (default)</h2>
        <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
      </section>
    );
  }

  const upd = (k: keyof typeof prefs) => (v: string | number) => setPrefs({ ...prefs, [k]: v } as typeof prefs);

  async function onSave() {
    if (!prefs) return;
    setSaving(true);
    try {
      await saveFn({ data: prefs });
      toast.success("Defaults guardados");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h2 className="mb-1 inline-flex items-center gap-2 text-sm font-semibold"><Utensils className="h-4 w-4" /> Preferências de refeição (default)</h2>
      <p className="mb-3 text-xs text-muted-foreground">Estas janelas são o default global. Cada utilizador pode dar override no seu roteiro.</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Field label="Almoço de" type="time" value={prefs.lunchStart} onChange={upd("lunchStart") as (v: string) => void} />
        <Field label="Almoço até" type="time" value={prefs.lunchEnd} onChange={upd("lunchEnd") as (v: string) => void} />
        <Field label="Jantar de" type="time" value={prefs.dinnerStart} onChange={upd("dinnerStart") as (v: string) => void} />
        <Field label="Jantar até" type="time" value={prefs.dinnerEnd} onChange={upd("dinnerEnd") as (v: string) => void} />
        <Field label="Tempo a comer (min)" type="number" value={String(prefs.eatMinutes)} onChange={(v) => upd("eatMinutes")(Number(v) || 90)} />
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:brightness-110 disabled:opacity-50"
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Guardar default
      </button>
    </section>
  );
}

function Field({ label, type, value, onChange }: { label: string; type: "time" | "number"; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
      />
    </label>
  );
}


type TopKind = "restaurants" | "hotels" | "attractions";
type TopRow = { name: string; city: string; country: string | null; count: number; uniqueUsers: number };

function TopPlacesSection() {
  const fn = useServerFn(getTopPlaces);
  const [data, setData] = useState<Awaited<ReturnType<typeof getTopPlaces>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<TopKind>("restaurants");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [openPlace, setOpenPlace] = useState<{ name: string; kind: TopKind } | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    fn({ data: { city: city || undefined, country: country || undefined, fromDate: fromDate || undefined, toDate: toDate || undefined, limit: 25 } })
      .then(setData)
      .catch((e) => toast.error((e as Error).message))
      .finally(() => setLoading(false));
  }, [fn, city, country, fromDate, toDate]);

  useEffect(() => { reload(); }, [reload]);

  const tabs: { id: TopKind; label: string; icon: React.ReactNode }[] = [
    { id: "restaurants", label: "Restaurantes", icon: <Utensils className="h-3.5 w-3.5" /> },
    { id: "hotels", label: "Hotéis", icon: <BedDouble className="h-3.5 w-3.5" /> },
    { id: "attractions", label: "Atrações", icon: <Landmark className="h-3.5 w-3.5" /> },
  ];

  const list: TopRow[] = data?.[kind] ?? [];
  const kindLabel = tabs.find((t) => t.id === kind)?.label ?? kind;

  function buildFilterLabel() {
    const parts: string[] = [];
    if (city) parts.push(`cidade: ${city}`);
    if (country) parts.push(`país: ${country}`);
    if (fromDate) parts.push(`desde: ${fromDate}`);
    if (toDate) parts.push(`até: ${toDate}`);
    return parts.length ? parts.join(" · ") : "sem filtros";
  }

  function exportCsv() {
    if (!list.length) { toast.error("Nada para exportar"); return; }
    const header = ["rank", "name", "city", "country", "appearances", "unique_users"].join(",");
    const rows = list.map((p, i) => [
      i + 1,
      `"${p.name.replace(/"/g, '""')}"`,
      `"${p.city.replace(/"/g, '""')}"`,
      `"${(p.country ?? "").replace(/"/g, '""')}"`,
      p.count,
      p.uniqueUsers,
    ].join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `routify-top-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exportado");
  }

  function exportPdf() {
    if (!list.length) { toast.error("Nada para exportar"); return; }
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = margin;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`Routify · Top ${kindLabel}`, margin, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`Gerado em ${new Date().toLocaleDateString("pt-PT")} · ${buildFilterLabel()}`, margin, y);
    y += 8;
    doc.setTextColor(20);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("#", margin, y);
    doc.text("Nome / Cidade", margin + 8, y);
    doc.text("Aparições", pageW - margin - 38, y, { align: "right" });
    doc.text("Users únicos", pageW - margin, y, { align: "right" });
    y += 2;
    doc.setDrawColor(200);
    doc.line(margin, y, pageW - margin, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    list.forEach((p, i) => {
      if (y > 280) { doc.addPage(); y = margin; }
      doc.text(`${i + 1}`, margin, y);
      const name = doc.splitTextToSize(p.name, pageW - margin * 2 - 60)[0] ?? p.name;
      doc.setFont("helvetica", "bold");
      doc.text(name, margin + 8, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120);
      doc.text(`${p.city}${p.country ? ", " + p.country : ""}`, margin + 8, y + 4);
      doc.setTextColor(20);
      doc.text(String(p.count), pageW - margin - 38, y, { align: "right" });
      doc.text(String(p.uniqueUsers), pageW - margin, y, { align: "right" });
      y += 9;
    });
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text("Routify · routify-tour.com", margin, 290);
    doc.save(`routify-top-${kind}-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF exportado");
  }

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Locais mais escolhidos</h2>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1 rounded-full border border-input px-3 py-1 text-xs hover:bg-accent"
          >
            <FileDown className="h-3 w-3" /> CSV
          </button>
          <button
            type="button"
            onClick={exportPdf}
            className="inline-flex items-center gap-1 rounded-full border border-input px-3 py-1 text-xs hover:bg-accent"
          >
            <FileText className="h-3 w-3" /> PDF
          </button>
        </div>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Quantas vezes cada local aparece em rotas guardadas e quantos utilizadores diferentes o escolheram. Clica numa linha para ver detalhes.
      </p>

      {/* Filters */}
      <div className="mb-3 grid gap-2 sm:grid-cols-4">
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Cidade"
          className="rounded-lg border border-input bg-background px-3 py-1.5 text-xs"
        />
        <input
          type="text"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="País"
          className="rounded-lg border border-input bg-background px-3 py-1.5 text-xs"
        />
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="rounded-lg border border-input bg-background px-3 py-1.5 text-xs"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="rounded-lg border border-input bg-background px-3 py-1.5 text-xs"
        />
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setKind(t.id)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${
              kind === t.id ? "border-brand bg-brand/10 text-brand" : "border-input text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : list.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">Sem dados para os filtros aplicados.</p>
      ) : (
        <ul className="space-y-1">
          {list.map((p, i) => (
            <li key={`${p.name}-${p.city}-${i}`}>
              <button
                type="button"
                onClick={() => setOpenPlace({ name: p.name, kind })}
                className="flex w-full items-start justify-between gap-3 rounded-lg p-2 text-left text-sm transition hover:bg-accent"
              >
                <span className="flex min-w-0 items-start gap-2">
                  <span className="w-5 shrink-0 text-xs text-muted-foreground">#{i + 1}</span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{p.name}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {p.city}{p.country ? `, ${p.country}` : ""}
                    </span>
                  </span>
                </span>
                <span className="shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                  <span className="block font-semibold text-foreground">{p.count}×</span>
                  <span>{p.uniqueUsers} {p.uniqueUsers === 1 ? "user" : "users"}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <PlaceDetailDialog
        place={openPlace}
        filters={{ city, country, fromDate, toDate }}
        onClose={() => setOpenPlace(null)}
      />
    </section>
  );
}

function PlaceDetailDialog({
  place,
  filters,
  onClose,
}: {
  place: { name: string; kind: TopKind } | null;
  filters: { city: string; country: string; fromDate: string; toDate: string };
  onClose: () => void;
}) {
  const fn = useServerFn(getPlaceDetail);
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof getPlaceDetail>> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!place) { setDetail(null); return; }
    setLoading(true);
    fn({
      data: {
        name: place.name,
        city: filters.city || undefined,
        country: filters.country || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
      },
    })
      .then(setDetail)
      .catch((e) => toast.error((e as Error).message))
      .finally(() => setLoading(false));
  }, [place, filters, fn]);

  const mapsQuery = place ? encodeURIComponent(place.name) : "";
  const sample = detail?.sampleCoords;

  return (
    <Dialog open={!!place} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-6">{place?.name}</DialogTitle>
        </DialogHeader>
        {loading || !detail ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Aparições" value={detail.totalCount} />
              <StatCard label="Users únicos" value={detail.uniqueUsers} />
              <StatCard
                label="Primeira"
                value={detail.firstSeen ? new Date(detail.firstSeen).toLocaleDateString("pt-PT") : "—"}
              />
              <StatCard
                label="Última"
                value={detail.lastSeen ? new Date(detail.lastSeen).toLocaleDateString("pt-PT") : "—"}
              />
            </div>

            <div className="overflow-hidden rounded-xl border border-border">
              {sample ? (
                <iframe
                  title="map"
                  className="block h-64 w-full"
                  loading="lazy"
                  src={`https://maps.google.com/maps?q=${sample.lat},${sample.lng}&z=15&output=embed`}
                />
              ) : (
                <iframe
                  title="map"
                  className="block h-64 w-full"
                  loading="lazy"
                  src={`https://maps.google.com/maps?q=${mapsQuery}&output=embed`}
                />
              )}
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-end gap-1 border-t border-border bg-muted/30 px-3 py-1.5 text-[11px] text-brand hover:underline"
              >
                <ExternalLink className="h-3 w-3" /> Abrir no Google Maps
              </a>
            </div>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Por cidade
              </h3>
              <ul className="space-y-1">
                {detail.byCity.map((c) => (
                  <li key={`${c.city}-${c.country ?? ""}`} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-1.5 text-sm">
                    <span>
                      <MapPin className="mr-1 inline h-3 w-3 text-brand" />
                      {c.city}{c.country ? `, ${c.country}` : ""}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      <span className="font-semibold text-foreground">{c.count}×</span> · {c.uniqueUsers} users
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Histórico ({detail.occurrences.length})
              </h3>
              <ul className="max-h-48 space-y-0.5 overflow-y-auto text-[11px] text-muted-foreground">
                {detail.occurrences.map((o, i) => (
                  <li key={i} className="flex items-center justify-between border-b border-border/50 py-1">
                    <span>{o.city}{o.country ? `, ${o.country}` : ""}</span>
                    <span className="tabular-nums">{new Date(o.createdAt).toLocaleDateString("pt-PT")}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</div>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

// ============================================================
// TEAM TAB
// ============================================================
function TeamTab() {
  const listFn = useServerFn(listAdmins);
  const addFn = useServerFn(addAdmin);
  const removeFn = useServerFn(removeAdmin);
  const [admins, setAdmins] = useState<Awaited<ReturnType<typeof listAdmins>>["admins"]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setAdmins((await listFn()).admins); }
    catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  }, [listFn]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const res = await addFn({ data: { email: email.trim() } });
      if (!res.ok) { toast.error(res.error ?? "Erro"); return; }
      toast.success("Admin adicionado");
      setEmail("");
      await load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  }

  async function handleRemove(userId: string) {
    setBusyId(userId);
    try {
      await removeFn({ data: { userId } });
      toast.success("Admin removido");
      await load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleAdd} className="rounded-xl border border-border bg-card p-4">
        <h2 className="mb-2 text-sm font-semibold">Adicionar admin</h2>
        <p className="mb-3 text-xs text-muted-foreground">
          O utilizador tem de ter criado conta primeiro. Owners não podem ser removidos por aqui.
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@dominio.com"
            className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm"
            required
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Adicionar
          </button>
        </div>
      </form>

      <section>
        <h2 className="mb-3 text-sm font-semibold">Administradores atuais</h2>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-2">
            {admins.map((a) => {
              const isOwnerRow = a.roles.includes("owner");
              return (
                <div key={a.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{a.email || "(sem email)"}</p>
                    <div className="mt-0.5 flex gap-1">
                      {a.roles.map((r) => (
                        <Badge key={r} tone="brand">{r}</Badge>
                      ))}
                    </div>
                  </div>
                  {isOwnerRow ? (
                    <span className="text-xs text-muted-foreground"><ShieldOff className="mr-1 inline h-3 w-3" />protegido</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleRemove(a.id)}
                      disabled={busyId === a.id}
                      className="rounded-full border border-input px-3 py-1.5 text-xs hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"
                    >
                      {busyId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
